import React, { useState, useEffect, useRef } from "react";
import { Grid, Button, Typography, Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import JobCard from "../JobCard";
import axios from "axios";
import "./style.css";

export default function ViewInvoice(props) {
  const [jobDetails, setJobDetails] = useState({
    job_title: "",
    jobType: "",
    jobCategory: "",
    description: "",
    emergency: false,
    recurring: false,
    scheduledTime: "",
    location: "",
    jobId: "",
    status: ""
  });
  const [homeownerImage, setHomeownerImage] = useState(null);
  const [plumberImage, setPlumberImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showFullHomeownerImage, setShowFullHomeownerImage] = useState(false);
  const [showFullPlumberImage, setShowFullPlumberImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);

  const navigate = useNavigate();
  const { jobId } = useParams();

  // Fetch job details and images when the component mounts
  useEffect(() => {
    if (jobId) {
      // Fetch job details
      fetchJobDetails(jobId);
      
      // Fetch the job images
      fetchHomeownerImage(jobId);
      fetchPlumberImage(jobId);
    } else {
      console.log("No job ID in the URL.");
    }
  }, [jobId]);

  // Function to fetch job details
  const fetchJobDetails = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      // For demo purposes, we're setting hardcoded values
      // In a real application, you would fetch this data from an API
      setJobDetails({
        job_title: "Plumbing Job again",
        jobType: "Plumbing",
        jobCategory: "Repair",
        description: "small Toilet pipe",
        emergency: true,
        recurring: false,
        scheduledTime: "2025-01-28T14:30:00",
        location: "Sunnypark 112",
        jobId: id,
        status: "IN_PROGRESS" // Initial status
      });
      
      // Uncomment for real API call
      /*
      const response = await axios.get(`http://localhost:8081/api/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.data) {
        setJobDetails(response.data);
      }
      */
    } catch (err) {
      console.error("Error fetching job details:", err);
      setError("Failed to load job details. " + (err.response?.data?.message || err.message));
    }
  };

  // Function to fetch homeowner image
  const fetchHomeownerImage = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}/image`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob' // Important for image data
      });
      
      if (response.data) {
        // Create a URL for the blob data
        const imageUrl = URL.createObjectURL(response.data);
        setHomeownerImage(imageUrl);
      }
    } catch (err) {
      console.error("Error fetching homeowner image:", err);
      // Don't set error here, as this might be optional
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch plumber image
  const fetchPlumberImage = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}/plumber-image`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob' // Important for image data
      });
      
      if (response.data) {
        // Create a URL for the blob data
        const imageUrl = URL.createObjectURL(response.data);
        setPlumberImage(imageUrl);
      }
    } catch (err) {
      console.error("Error fetching plumber image:", err);
      // Don't set error here, as this might be optional
    }
  };

  // Function to update job status to COMPLETED
  const updateJobStatus = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.put(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}/status/completed`,
        { status: "COMPLETED" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        // Update local state
        setJobDetails(prev => ({
          ...prev,
          status: "COMPLETED"
        }));
        setStatusUpdateSuccess(true);
      }
    } catch (err) {
      console.error("Error updating job status:", err);
      setError("Failed to update job status. " + (err.response?.data?.message || err.message));
    }
  };

  // Handle image selection
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Upload the selected image and update job status
  const uploadImage = async () => {
    if (!selectedImage) {
      setError("Please select an image to upload");
      return;
    }

    setLoading(true);
    setError(null);
    setUploadSuccess(false);
    setStatusUpdateSuccess(false);
    
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      // Create form data to send the file - changed from 'file' to 'files' to match server expectation
      const formData = new FormData();
      formData.append("files", selectedImage);
      
      // Upload the image to the server
      const response = await axios.post(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/upload-plumber-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Don't set Content-Type when sending FormData
          },
        }
      );
      
      if (response.status === 200 || response.status === 201) {
        // Image uploaded successfully
        setUploadSuccess(true);
        
        // Fetch the updated plumber image
        fetchPlumberImage(jobId);
        
        // Update job status to COMPLETED immediately after successful upload
        await updateJobStatus(jobId);
        
        // Clear the selected image and preview after successful upload
        setSelectedImage(null);
        setImagePreview(null);
      }
    } catch (err) {
      console.error("Error uploading job image:", err);
      setError("Failed to upload image. " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Event handler for "Generate Invoice" button
  const handleViewInvoice = () => {
    if (jobId) {
      // Navigate to the invoice page with the jobId as a parameter
      navigate(`/InvoicePage/${jobId}`);
    } else {
      console.log("Job ID is missing.");
    }
  };

  // Format the scheduledTime to display in a readable format
  const formatScheduledTime = (dateTime) => {
    if (!dateTime) return "";
    const date = new Date(dateTime);
    return date.toLocaleString();
  };

  // Get status badge color based on status
  const getStatusColor = (status) => {
    switch(status) {
      case "COMPLETED":
        return "#4caf50"; // Green
      case "IN_PROGRESS":
        return "#2196f3"; // Blue
      case "PENDING":
        return "#ff9800"; // Orange
      case "CANCELLED":
        return "#f44336"; // Red
      default:
        return "#757575"; // Grey
    }
  };

  return (
    <Grid container padding="3%">
      <Grid container>
        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            Job Details
          </Typography>
          <Typography variant="h4" sx={{ color: "text.secondary" }}>
            - {jobId}
          </Typography>
          <Box 
            sx={{ 
              display: 'inline-block',
              backgroundColor: getStatusColor(jobDetails.status),
              color: 'white',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              ml: 2
            }}
          >
            {jobDetails.status}
          </Box>
        </Grid>
        
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Grid>
            <div className="labelDivCls">
              <div className="labelCls">Job Title</div>
              <div className="valueCls">{jobDetails.job_title}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Type</div>
              <div className="valueCls">{jobDetails.jobType}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Category</div>
              <div className="valueCls">{jobDetails.jobCategory}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Description</div>
              <div className="valueCls">{jobDetails.description}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Location</div>
              <div className="valueCls">{jobDetails.location}</div>
            </div>
          </Grid>
        </Grid>

        {props.showJobCard && (
          <Grid>
            <JobCard />
          </Grid>
        )}

        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Grid>
            <div className="labelCls">Is this an emergency?</div>
            <div className="valueCls">{jobDetails.emergency ? "Yes" : "No"}</div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Date & Time</div>
              <div className="valueCls">{formatScheduledTime(jobDetails.scheduledTime)}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Is this a recurring issue?</div>
              <div className="valueCls">{jobDetails.recurring ? "Yes" : "No"}</div>
            </div>
          </Grid>
        </Grid>

        {/* Homeowner Image Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Image by Homeowner
          </Typography>
          
          {loading && <div>Loading image...</div>}
          
          {homeownerImage ? (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img 
                src={homeownerImage} 
                alt="Homeowner's Image" 
                style={{ 
                  maxWidth: showFullHomeownerImage ? '100%' : '300px', 
                  maxHeight: showFullHomeownerImage ? 'auto' : '200px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setShowFullHomeownerImage(!showFullHomeownerImage)}
              />
              {showFullHomeownerImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Click the image to resize
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>No image provided by homeowner</Typography>
          )}
        </Grid>

        {/* Plumber Image Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Image by Plumber
          </Typography>
          
          {plumberImage ? (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img 
                src={plumberImage} 
                alt="Plumber's Image" 
                style={{ 
                  maxWidth: showFullPlumberImage ? '100%' : '300px', 
                  maxHeight: showFullPlumberImage ? 'auto' : '200px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setShowFullPlumberImage(!showFullPlumberImage)}
              />
              {showFullPlumberImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Click the image to resize
                </Typography>
              )}
            </Box>
          ) : (
            <Typography>No image uploaded by plumber yet</Typography>
          )}

          {/* Image Upload Section - only show if job is not already completed */}
          {jobDetails.status !== "COMPLETED" && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id="image-upload"
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="contained"
                    component="span"
                    color="primary"
                  >
                    Select Image
                  </Button>
                </label>
                {imagePreview && (
                  <div>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ width: '150px', height: '150px', marginTop: '10px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                    <Typography variant="caption" display="block" style={{ marginTop: '4px' }}>
                      {selectedImage ? selectedImage.name : ""}
                    </Typography>
                  </div>
                )}
              </Grid>
              
              {selectedImage && (
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    color="success" 
                    onClick={uploadImage}
                    disabled={loading}
                    sx={{ mt: 1 }}
                  >
                    {loading ? "Uploading..." : "Upload Image & Complete Job"}
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Note: Uploading an image will mark this job as COMPLETED
                  </Typography>
                </Grid>
              )}
              
              {error && (
                <Grid item xs={12}>
                  <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>
                </Grid>
              )}
              
              {uploadSuccess && (
                <Grid item xs={12}>
                  <div style={{ color: 'green', marginTop: '10px' }}>
                    Image uploaded successfully!
                  </div>
                </Grid>
              )}
              
              {statusUpdateSuccess && (
                <Grid item xs={12}>
                  <div style={{ color: 'green', marginTop: '10px' }}>
                    Job status updated to COMPLETED!
                  </div>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>

        {/* Button for "Generate Invoice" */}
        <Grid item xs={12} sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleViewInvoice}
          >
            Generate Invoice
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}